import React, { FC, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Paragraph,
  Heading,
  Flex,
  Separator,
  Tooltip,
  Show,
} from '@wunder-mobility/ui-components';
import classNames from 'classnames';
import { useNavigate } from 'react-router';
import { Subscription } from '@/features/subscriptions/types/Subscription.types';
import {
  getSubscriptionBadge,
  getSubscriptionCTA,
  isSubscriptionActiveOrExpiring,
  isSubscriptionExpiring,
  getBillingCycleLabel,
  getRenewalPeriodLabel,
} from '@/features/subscriptions/utils/subscriptionHelpers';
import { useModal } from '@/core/components/UI/modals/ModalProvider';
import ManageSubscriptionModal from '@/features/subscriptions/components/modals/ManageSubscriptionModal';
import PurchaseConfirmationModal from '@/features/subscriptions/components/modals/PurchaseConfirmationModal';
import SubscriptionBenefits from './SubscriptionBenefits';
import styles from '@/features/subscriptions/assets/scss/SubscriptionTile.module.scss';
import CurrencyDisplay from '@/core/components/CurrencyDisplay';
import { accountEditPaymentMethods } from '@/core/pages/Account/routes';

interface SubscriptionTileProps {
  subscription: Subscription;
  hasActiveSubscription?: boolean;
}

const SubscriptionTile: FC<SubscriptionTileProps> = ({
  subscription,
  hasActiveSubscription = false,
}) => {
  const { showModal, hideModal } = useModal();
  const navigate = useNavigate();
  const badge = getSubscriptionBadge(subscription);
  const ctaType = getSubscriptionCTA(subscription);

  const cycleLabel = getBillingCycleLabel(subscription.interval);
  const renewalPeriod = getRenewalPeriodLabel(subscription.interval);

  const handleCTAClick = () => {
    if (ctaType === 'manage') {
      showModal(ManageSubscriptionModal, {
        subscription,
        onChangePaymentMethod: () => {
          navigate(accountEditPaymentMethods.path);
        },
        onCancel: () => hideModal(),
      });
    } else {
      showModal(PurchaseConfirmationModal, {
        subscription,
        onChangePaymentMethod: () => {
          navigate(accountEditPaymentMethods.path);
        },
        onCancel: () => hideModal(),
      });
    }
  };

  const isThisTileActiveOrExpiring = useMemo(
    () => isSubscriptionActiveOrExpiring(subscription),
    [subscription],
  );

  const isExpiring = useMemo(() => isSubscriptionExpiring(subscription), [subscription]);

  const isDisabled = hasActiveSubscription && !isThisTileActiveOrExpiring;

  const getButtonText = () => {
    if (ctaType === 'manage') {
      return <Trans id="subscriptions.cta.manage">Manage</Trans>;
    }
    if (ctaType === 'select' && hasActiveSubscription) {
      return <Trans id="subscriptions.cta.switchPlan">Switch to this Plan</Trans>;
    }
    return <Trans id="subscriptions.cta.selectPlan">Select Plan</Trans>;
  };

  const tileClasses = classNames(styles['subscription-tile'], {
    [styles['subscription-tile--highlighted']]:
      subscription.highlight && badge === 'popular' && !hasActiveSubscription,
    [styles['subscription-tile--active']]: isThisTileActiveOrExpiring && !isExpiring,
    [styles['subscription-tile--expiring']]: isExpiring,
    [styles['subscription-tile--disabled']]: isDisabled,
  });

  const badgeClasses = classNames(styles['subscription-tile__badge'], {
    [styles['subscription-tile__badge--popular']]: badge === 'popular',
    [styles['subscription-tile__badge--active']]: badge === 'active',
    [styles['subscription-tile__badge--expiring']]: badge === 'expiring',
  });

  return (
    <Flex direction="column" className={tileClasses} justify="space-between">
      <Flex direction="column" gap="lg">
        <Show when={!!badge}>
          <Flex className={badgeClasses}>
            <Show when={badge === 'popular'}>
              <Paragraph size={4} textColor="text-0" marginBottom={0} align="center">
                <Trans id="subscriptions.badge.mostPopular">Most Popular</Trans>
              </Paragraph>
            </Show>
            <Show when={badge === 'active'}>
              <Paragraph size={4} textColor="text-0" marginBottom={0} align="center">
                <Trans id="subscriptions.badge.activePlan">Active Plan</Trans>
              </Paragraph>
            </Show>
            <Show when={badge === 'expiring'}>
              <Paragraph size={4} textColor="text-0" marginBottom={0} align="center">
                <Trans id="subscriptions.badge.expiring">Expiring Plan</Trans>
              </Paragraph>
            </Show>
          </Flex>
        </Show>

        <Flex direction="column" gap="sm">
          <Heading size={6} marginBottom={10}>
            {subscription.description || subscription.code}
          </Heading>

          <Flex align="flex-end">
            <Heading size={2}>
              <CurrencyDisplay value={subscription.price} currency={subscription.currency} />
            </Heading>
            <Paragraph size={4} textColor="text-400" marginBottom={16}>
              {cycleLabel}
            </Paragraph>
          </Flex>
        </Flex>

        <SubscriptionBenefits subscription={subscription} />
      </Flex>

      <Flex direction="column" gap="sm">
        <Separator />
        <Paragraph size={4} textColor="text-400">
          <Trans id="subscriptions.disclaimer.renewal">
            Renews automatically every {renewalPeriod} until cancelled.
          </Trans>
        </Paragraph>
        <Paragraph size={4} textColor="text-400">
          <Trans id="subscriptions.disclaimer.funds">
            Purchased funds don't expire and are not <br /> transferable.
          </Trans>
        </Paragraph>
        {isDisabled ? (
          <Tooltip
            placement="top"
            content={
              <Trans id="subscriptions.tooltip.cancelBeforeSwitch">
                Your current subscription needs to be canceled and expired before purchasing another
                plan.
              </Trans>
            }
          >
            <Button
              width="full"
              variation={ctaType === 'manage' ? 'secondary' : 'primary'}
              onClick={handleCTAClick}
              disabled
            >
              {getButtonText()}
            </Button>
          </Tooltip>
        ) : (
          <Button
            width="full"
            variation={ctaType === 'manage' ? 'secondary' : 'primary'}
            onClick={handleCTAClick}
          >
            {getButtonText()}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default SubscriptionTile;
